import React from 'react';
import { Outlet, useParams, useLocation } from 'react-router-dom';
import { AssistantLayout } from '../../components/layout/AssistantLayout';
import { useUserState } from '@features/user';
import { Actions, useSuggestedActionsCount } from '../../features/actions';
import { NotificationCards, useNotifications } from '../../features/notifications';
import { useUIState } from '../../store/hooks';

interface ActionsBarLayoutProps {
  children?: React.ReactNode;
}

/**
 * Layout component for routes that need the actions sidebar
 * Currently used for note view routes
 *
 * Can be used in two ways:
 * 1. As a route element with <Outlet /> (default)
 * 2. As a wrapper component with children prop
 */
export const ActionsBarLayout: React.FC<ActionsBarLayoutProps> = ({ children }) => {
  const userState = useUserState();
  const { setSidebarOpen } = useUIState();
  const { noteId, jotdocId } = useParams<{ noteId?: string; jotdocId?: string }>();
  const location = useLocation();

  // Hide actions sidebar when on jotdoc route or edit-template route
  const isJotdocRoute = Boolean(jotdocId) || location.pathname.startsWith('/jotdoc/');
  const isEditTemplateRoute = location.pathname.includes('/edit-template');

  const actionsEnabled = userState?.featureFlags?.actions && !isJotdocRoute && !isEditTemplateRoute;

  const count = useSuggestedActionsCount({ noteId });
  const { unreadCount: notificationCount } = useNotifications();
  const prevCountRef = React.useRef(count);

  React.useEffect(() => {
    if (!actionsEnabled) {
      return;
    }
    if (prevCountRef.current < count) {
      setSidebarOpen(true);
    }
    if (count === 0 && prevCountRef.current !== 0) {
      setSidebarOpen(false);
    }
    prevCountRef.current = count;
  }, [actionsEnabled, count, setSidebarOpen]);

  const sidebarContent = (
    <>
      {notificationCount > 0 ? <NotificationCards /> : null}
      {actionsEnabled && !isJotdocRoute && !isEditTemplateRoute ? <Actions /> : null}
    </>
  );

  // Hide sidebar completely on jotdoc routes or edit-template route, otherwise show if actions or notifications exist
  const showSidebar = !isJotdocRoute && !isEditTemplateRoute && (actionsEnabled || notificationCount > 0);

  return (
    <AssistantLayout
      main={children || <Outlet />}
      sidebar={showSidebar ? sidebarContent : undefined}
      notificationCount={count + notificationCount}
    />
  );
};
